import classNames from 'classnames';
import React from 'react';
import { normalizeIcon, PathIcon } from '../utils/icons';
import borderIcons from './borderIcons/icons';
import styles from './businessProcess.module.css';
import {
  getBusinessProcessDefinition,
  type BusinessProcessShapeNames,
} from './definitions';
import { type IconWithOffset } from '../types';
import comboIcons from './comboIcons/icons';

interface BusinessProcessIconProps {
  shapeName: BusinessProcessShapeNames;
  variant: string;
}
export const BusinessProcessIcon = ({
  shapeName,
  variant,
}: BusinessProcessIconProps) => {
  const definition = getBusinessProcessDefinition(shapeName, variant);
  const { iconName, secondaryIconName, borderStyle } =
    definition?.properties ?? {};

  // ignore offsets for the icons
  const icon = iconName ? normalizeIcon(iconName) : undefined;
  const secondaryIcon = secondaryIconName
    ? normalizeIcon(secondaryIconName)
    : undefined;

  const borderIcon = borderIcons[shapeName][borderStyle ?? 'default'];

  return (
    <div
      className={classNames(
        styles.icon,
        styles[shapeName],
        borderStyle && styles[borderStyle]
      )}
    >
      {borderIcon && (
        <PathIcon
          icon={{ type: 'svg', svg: borderIcon }}
          className={styles.background}
          fillContainer={true}
        />
      )}
      {icon && (
        <PathIcon
          icon={icon.reference}
          className={styles.primary}
          fillContainer={true}
        />
      )}
      {secondaryIcon && (
        <PathIcon
          icon={secondaryIcon.reference}
          className={styles.secondary}
          fillContainer={true}
        />
      )}
    </div>
  );
};

export const BusinessProcessCategoryIcon = ({
  shapeName,
  variant,
}: BusinessProcessIconProps) => {
  let borderIcon: string;
  if (shapeName === 'businessProcessDataStore') {
    // use data icon as category for datastore
    borderIcon = borderIcons.businessProcessData.default;
  } else {
    const definition = getBusinessProcessDefinition(shapeName, variant);
    const borderStyle = definition?.properties?.borderStyle ?? 'default';
    borderIcon = borderIcons[shapeName][borderStyle];
  }

  return (
    <div className={styles.icon}>
      <PathIcon
        icon={{ type: 'svg', svg: borderIcon }}
        className={styles.background}
        fillContainer={true}
      />
    </div>
  );
};

const getTypeIcon = (
  shapeName: BusinessProcessShapeNames,
  variant: string
): IconWithOffset | undefined => {
  if (shapeName === 'businessProcessDataStore') {
    return { type: 'svg', svg: borderIcons.businessProcessDataStore.default };
  } else if (
    shapeName === 'businessProcessData' &&
    variant === 'data-input-collection'
  ) {
    return { type: 'svg', svg: comboIcons.arrowRight_Lines };
  } else if (
    shapeName === 'businessProcessData' &&
    variant === 'data-output-collection'
  ) {
    return { type: 'svg', svg: comboIcons.arrowRightSolid_Lines };
  } else {
    const { properties } =
      getBusinessProcessDefinition(shapeName, variant) ?? {};
    return properties?.iconName ?? properties?.secondaryIconName;
  }
};

export const BusinessProcessTypeIcon = ({
  shapeName,
  variant,
}: BusinessProcessIconProps) => {
  const icon = getTypeIcon(shapeName, variant);

  return icon ? (
    <div className={styles.icon}>
      <PathIcon
        icon={icon}
        className={styles.background}
        fillContainer={true}
      />
    </div>
  ) : (
    <>—</>
  );
};
